import type { CallHistoryMethodAction } from 'connected-react-router';
import type { Action, Reducer } from 'redux';

import { IAnalyticsAction, IApplicationState, IAppThunkAction } from '.';
import { GoogleTagManagerEvents } from '../helpers/googleTagManager';
import { Routes, navigateTo } from '../helpers/routes';
import IPaymentGateway, { PaymentGateway } from '../interfaces/IPaymentGateway';
import PaymentServices from '../service/payments';

import { PurchaseTypes } from './PurchaseTypes';
import { actionCreators as UIActions } from '../store/UI';
import Logging from '../service/logging';
import {
    IDiscountCodeApplied,
    IClearBookingAction,
    IExpireBookingSuccessAction,
    IExpireBookingCompletedAction,
    DISCOUNT_CODE_APPLIED,
    EXPIRE_BOOKING_SUCCESS,
    EXPIRE_BOOKING_COMPLETE,
    CLEAR_BOOKING,
    DISCOUNT_CODE_REMOVED,
    IDiscountCodeRemoved,
    UPDATE_ADDONS_COMPLETE,
    IUpdateAddOnsCompleteAction,
    UPDATE_BOOKING_REQUESTED,
    IUpdateBookingRequestedAction,
    UPDATE_PAYMENT_OPTION_REQUESTED,
    IUpdatePaymentOptionRequestedAction,
} from './Booking';
import {
    IUpdateGiftVoucherAmountSuccessAction,
    IClearGiftVoucherAction,
    ICancelGiftVoucherSuccessAction,
    ICancelGiftVoucherCompleteAction,
    UPDATE_GIFT_VOUCHER_AMOUNT_SUCCESS,
    CLEAR_GIFT_VOUCHER,
    CANCEL_GIFT_VOUCHER_SUCCESS,
    CANCEL_GIFT_VOUCHER_COMPLETE,
} from './GiftVoucher';
import BrazeEvents from '../helpers/braze';
 import { appInsights } from "../Logging/appInsights";
import { ISetSelectedPlayersAction, SET_SELECTED_PLAYERS } from './PremiumPtv';
import { ISiteSelectedAction, SITE_SELECTED } from './Sites';
import { IInvoiceCodeRemovedAction, IInvoiceCodeSuccessAction, INVOICE_CODE_REMOVED, INVOICE_CODE_SUCCESS } from './Invoice';

export interface IPaymentState {
    stripe: IStripePayment | undefined;
    paypal: IPayPalPayment | undefined;
    checkout: ICheckoutPayment | undefined;
    custom: ICustomPayment | undefined;
    loading: boolean;
    gatewaysLoading: boolean;
    backProcessing: boolean;
    clientSecret?: string;
}

export interface IPayment {
    apiKey?: string;
    paymentMethodType?: string;
}

// Redirect-based generic gateway. Has no apiKey; instead carries the provider's
// display name/image and the URL the user is redirected to in order to pay.
export interface ICustomPayment {
    gatewayName?: string;
    imageUrl?: string;
    paymentUrl?: string;
    paymentMethodType?: string;
}

export enum PaymentTabKey {
	Stripe = 'stripe',
	Paypal = 'paypal',
	Checkout = 'checkout',
	Custom = 'custom',
	Reserve = 'reserve',
}

export enum PaymentMethodTypes {
    Unknown = 1,
    CreditCard = 2,
    Credit = 3,
    Paypal = 4,
    FreedomPay = 6,
    GiftVoucher = 7,
    CodeVoucher = 8,
    Stripe = 9,
    Checkout = 10,
    Custom = 17,
}

export type IStripePayment = IPayment;
export type IPayPalPayment = IPayment;
export type ICheckoutPayment = IPayment;

const PAYPAL_PAYMENT_REQUESTED: 'PAYPAL_PAYMENT_REQUESTED' = 'PAYPAL_PAYMENT_REQUESTED';
const PAYPAL_PAYMENT_COMPLETED: 'PAYPAL_PAYMENT_COMPLETED' = 'PAYPAL_PAYMENT_COMPLETED';
const PAYPAL_PAYMENT_FAILED: 'PAYPAL_PAYMENT_FAILED' = 'PAYPAL_PAYMENT_FAILED';
const PAYPAL_PAYMENT_CANCELLED: 'PAYPAL_PAYMENT_CANCELLED' = 'PAYPAL_PAYMENT_CANCELLED';
const PAYPAL_PAYMENT_SUCCESS: 'PAYPAL_PAYMENT_SUCCESS' = 'PAYPAL_PAYMENT_SUCCESS';

const CHECKOUT_PAYMENT_REQUESTED: 'CHECKOUT_PAYMENT_REQUESTED' = 'CHECKOUT_PAYMENT_REQUESTED';
const CHECKOUT_PAYMENT_COMPLETED: 'CHECKOUT_PAYMENT_COMPLETED' = 'CHECKOUT_PAYMENT_COMPLETED';
const CHECKOUT_PAYMENT_FAILED: 'CHECKOUT_PAYMENT_FAILED' = 'CHECKOUT_PAYMENT_FAILED';
const CHECKOUT_PAYMENT_SUCCESS: 'CHECKOUT_PAYMENT_SUCCESS' = 'CHECKOUT_PAYMENT_SUCCESS';

const CUSTOM_PAYMENT_REQUESTED: 'CUSTOM_PAYMENT_REQUESTED' = 'CUSTOM_PAYMENT_REQUESTED';

const STRIPE_PAYMENT_REQUESTED: 'STRIPE_PAYMENT_REQUESTED' = 'STRIPE_PAYMENT_REQUESTED';
const STRIPE_PAYMENT_COMPLETED: 'STRIPE_PAYMENT_COMPLETE' = 'STRIPE_PAYMENT_COMPLETE';
const STRIPE_PAYMENT_SUCCESS: 'STRIPE_PAYMENT_SUCCESS' = 'STRIPE_PAYMENT_SUCCESS';
const STRIPE_PAYMENT_FAILED: 'STRIPE_PAYMENT_FAILED' = 'STRIPE_PAYMENT_FAILED';
const STRIPE_PAYMENT_ACTION_REQUIRED: 'STRIPE_PAYMENT_ACTION_REQUIRED' = 'STRIPE_PAYMENT_ACTION_REQUIRED';

const GET_PAYMENT_GATEWAYS_REQUESTED: 'GET_PAYMENT_GATEWAYS_REQUESTED' = 'GET_PAYMENT_GATEWAYS_REQUESTED';
export const GET_PAYMENT_GATEWAYS_SUCCESS: 'GET_PAYMENT_GATEWAYS_SUCCESS' = 'GET_PAYMENT_GATEWAYS_SUCCESS';
export const GET_PAYMENT_GATEWAYS_COMPLETED: 'GET_PAYMENT_GATEWAYS_COMPLETED' = 'GET_PAYMENT_GATEWAYS_COMPLETED';
const GET_PAYMENT_GATEWAYS_FAILED: 'GET_PAYMENT_GATEWAYS_FAILED' = 'GET_PAYMENT_GATEWAYS_FAILED';

const BACK_PROCESSING_ADDED: 'BACK_PROCESSING_ADDED' = 'BACK_PROCESSING_ADDED';

const VALID_PAYMENT_DETAILS: 'VALID_PAYMENT_DETAILS' = 'VALID_PAYMENT_DETAILS';
const VIEWED_TERMS_OF_SERVICE: 'VIEWED_TERMS_OF_SERVICE' = 'VIEWED_TERMS_OF_SERVICE';
const VIEWED_PRIVACY_POLICY: 'VIEWED_PRIVACY_POLICY' = 'VIEWED_PRIVACY_POLICY';
const CLOSED_SURVEY: 'CLOSED_SURVEY' = 'CLOSED_SURVEY';

const UPDATE_CLIENT_SECRET: 'UPDATE_CLIENT_SECRET' = 'UPDATE_CLIENT_SECRET';
const CLEAR_CLIENT_SECRET: 'CLEAR_CLIENT_SECRET' = 'CLEAR_CLIENT_SECRET';

/// --------------------------------
/// GET GATEWAY INTERFACES
/// --------------------------------

export interface IGetPaymentGatewaysRequestedAction extends Action {
    type: typeof GET_PAYMENT_GATEWAYS_REQUESTED;
}

export interface IGetPaymentGatewaysSuccessAction extends Action {
    type: typeof GET_PAYMENT_GATEWAYS_SUCCESS;
    data: Array<IPaymentGateway>;
}

export interface IGetPaymentGatewaysCompletedAction extends Action {
    type: typeof GET_PAYMENT_GATEWAYS_COMPLETED;
}

export interface IGetPaymentGatewaysFailedAction extends Action {
    type: typeof GET_PAYMENT_GATEWAYS_FAILED;
}

/// --------------------------------
/// STRIPE INTERFACES
/// --------------------------------

export interface IStripePaymentRequestedAction extends IAnalyticsAction {
    type: typeof STRIPE_PAYMENT_REQUESTED;
}

export interface IStripePaymentCompleteAction extends Action {
    type: typeof STRIPE_PAYMENT_COMPLETED;
}

export interface IStripePaymentActionRequiredAction extends Action {
    type: typeof STRIPE_PAYMENT_ACTION_REQUIRED;
    responseSecret: string;
}

export interface IStripePaymentSuccessAction extends Action {
    type: typeof STRIPE_PAYMENT_SUCCESS;
}

export interface IStripePaymentFailedAction extends IAnalyticsAction {
    type: typeof STRIPE_PAYMENT_FAILED;
}

/// --------------------------------
/// PAYPAL INTERFACES
/// --------------------------------

export interface IPaypalPaymentRequestedAction extends IAnalyticsAction {
    type: typeof PAYPAL_PAYMENT_REQUESTED;
}
export interface IPaypalPaymentSuccessAction extends Action {
    type: typeof PAYPAL_PAYMENT_SUCCESS;
}

export interface IPaypalPaymentCompletedAction extends Action {
    type: typeof PAYPAL_PAYMENT_COMPLETED;
}

export interface IPaypalPaymentFailedAction extends Action {
    type: typeof PAYPAL_PAYMENT_FAILED;
}

export interface IPaypalPaymentCancelledAction extends Action {
    type: typeof PAYPAL_PAYMENT_CANCELLED;
}

/// --------------------------------
/// CHECKOUT INTERFACES
/// --------------------------------

export interface ICheckoutPaymentRequestedAction extends IAnalyticsAction {
    type: typeof CHECKOUT_PAYMENT_REQUESTED;
}
export interface ICheckoutPaymentSuccessAction extends Action {
    type: typeof CHECKOUT_PAYMENT_SUCCESS;
}

export interface ICheckoutPaymentCompletedAction extends Action {
    type: typeof CHECKOUT_PAYMENT_COMPLETED;
}

export interface ICheckoutPaymentFailedAction extends Action {
    type: typeof CHECKOUT_PAYMENT_FAILED;
}

/// --------------------------------
/// CUSTOM INTERFACES
/// --------------------------------

export interface ICustomPaymentRequestedAction extends IAnalyticsAction {
    type: typeof CUSTOM_PAYMENT_REQUESTED;
}

export interface IBackProcessingAddedAction extends Action {
    type: typeof BACK_PROCESSING_ADDED;
}

export interface IUpdateClientSecretAction extends Action {
    type: typeof UPDATE_CLIENT_SECRET;
    clientSecret?: string;
}

export interface IClearClientSecretAction extends Action {
    type: typeof CLEAR_CLIENT_SECRET;
}

export type KnownPaymentActions =
    | IGetPaymentGatewaysRequestedAction
    | IGetPaymentGatewaysCompletedAction
    | IGetPaymentGatewaysFailedAction
    | IGetPaymentGatewaysSuccessAction
    | IPaypalPaymentRequestedAction
    | IPaypalPaymentFailedAction
    | IPaypalPaymentCancelledAction
    | IPaypalPaymentSuccessAction
    | IPaypalPaymentCompletedAction
    | ICheckoutPaymentRequestedAction
    | ICheckoutPaymentFailedAction
    | ICheckoutPaymentSuccessAction
    | ICheckoutPaymentCompletedAction
    | ICustomPaymentRequestedAction
    | IStripePaymentRequestedAction
    | IStripePaymentCompleteAction
    | IStripePaymentSuccessAction
    | IStripePaymentFailedAction
    | IStripePaymentActionRequiredAction
    | IUpdateClientSecretAction
    | IClearClientSecretAction
    | CallHistoryMethodAction
    | IBackProcessingAddedAction
    | IDiscountCodeApplied
    | IDiscountCodeRemoved
    | IClearBookingAction
    | IExpireBookingSuccessAction
    | IExpireBookingCompletedAction
    | IUpdateGiftVoucherAmountSuccessAction
    | IClearGiftVoucherAction
    | ICancelGiftVoucherSuccessAction
    | ICancelGiftVoucherCompleteAction
    | ISetSelectedPlayersAction
    | ISiteSelectedAction
    | IInvoiceCodeSuccessAction
    | IInvoiceCodeRemovedAction
    | IUpdateAddOnsCompleteAction
    | IUpdateBookingRequestedAction
    | IUpdatePaymentOptionRequestedAction;

export const actionCreators = {
    getPaymentGateways: (siteId :number, currencyTypeId?: number): IAppThunkAction<KnownPaymentActions> => (dispatch, getState) => {
        const appState: IApplicationState = getState();
        if ( appState && siteId)
        {
            const purchaseType = appState.purchaseType.selectedPurchaseType;

            PaymentServices.GetPaymentGateways(siteId, purchaseType, currencyTypeId)
                .then((data) => {
                    dispatch({ type: GET_PAYMENT_GATEWAYS_SUCCESS, data: data });
                    dispatch({ type: GET_PAYMENT_GATEWAYS_COMPLETED });
                })
                .catch((error: Error) => {
                    dispatch({ type: GET_PAYMENT_GATEWAYS_FAILED });
                    dispatch({ type: GET_PAYMENT_GATEWAYS_COMPLETED });
                     appInsights?.trackException(
                         { error: new Error(`Error getting payment gateways`), severityLevel: 5},
                         { exception: error, sourceFile: "payments.ts", product: "booking" });
                });

            dispatch({ type: GET_PAYMENT_GATEWAYS_REQUESTED });
        }
    },

    stripePaymentStarted: (): IAppThunkAction<KnownPaymentActions> => (dispatch, getState) => {
        const appState: IApplicationState = getState();
        const charge =
            appState.purchaseType.selectedPurchaseType === PurchaseTypes.GiftVoucher
                ? appState.giftVoucher.charge
                : appState.booking.charge;
        const isEmailSubscribed = appState.customer?.emailSubscribed ?? false;
        const isSmsSubscribed = appState.customer?.smsSubscribed ?? false;

        const phoneCountryCode = appState.customer?.countryCode;

        const isReservation = appState?.sites?.selectedSite?.reservationPortal ?? false;
        const isGiftVoucher =
            appState?.purchaseType?.selectedPurchaseType === PurchaseTypes.GiftVoucher ||
            appState?.purchaseType?.selectedPurchaseType === PurchaseTypes.GlobalGiftVoucher;


        const country = appState?.sites?.selectedSite?.country ?? 'Unknown';
        const siteName = appState?.sites?.selectedSite?.name ?? 'Unknown';

        dispatch({
            type: STRIPE_PAYMENT_REQUESTED,
        });

        if (charge) {
            BrazeEvents.PaymentInitiated(
                isEmailSubscribed,
                isSmsSubscribed,
                phoneCountryCode,
                isReservation,
                isGiftVoucher,
                charge,
                country,
                siteName
            );
        }
    },
    stripePaymentRequested: (_emailAddress: string) =>
        ({ type: STRIPE_PAYMENT_REQUESTED } as IStripePaymentRequestedAction),
    stripePaymentFailed: () =>
        ({
            type: STRIPE_PAYMENT_FAILED,
            googleTagManagerEvent: GoogleTagManagerEvents.PaymentFailed('stripe'),
        } as IStripePaymentFailedAction),
    stripePaymentSuccess: () => ({ type: STRIPE_PAYMENT_SUCCESS } as IStripePaymentSuccessAction),
    stripePaymentStatus:
        (paymentIntent: string): IAppThunkAction<KnownPaymentActions> =>
        (dispatch: any, getState) => {
            const appState: IApplicationState = getState();

            const purchaseType = appState.purchaseType.selectedPurchaseType ?? undefined;

            const purchaseIsBooking = purchaseType === PurchaseTypes.Booking || purchaseType === PurchaseTypes.PrivateEvent

            const siteId = purchaseIsBooking
                    ? appState.booking.booking?.siteId
                    : appState.giftVoucher.giftVoucher?.siteId;

            const invoiceId = purchaseIsBooking
                    ? appState.booking.booking?.invoiceId
                    : appState.giftVoucher.giftVoucher?.invoiceId;

            const currencyTypeId = appState.giftVoucher?.selectedVoucherCurrency?.id ?? appState.sites?.selectedSite?.currencyId;

            if (siteId && invoiceId) {
                console.log(`running check stripe payment status from payments store with full context`);
                PaymentServices.StripePaymentStatus(paymentIntent, siteId, invoiceId, currencyTypeId, purchaseType).then((data) => {
                    if (data.failureCode != null) {
                        let newError = new Error(data.failureMessage);
                        newError.name = 'Stripe Payment Error';
                        Logging.LogError(newError);
                        dispatch(UIActions.displayError('errors.payments.declined'));
                        dispatch(actionCreators.stripePaymentFailed());
                        dispatch(navigateTo(Routes.Checkout, appState.sites.selectedSite?.urlKey));
                    }
                });
            }
        },

    paypalPaymentStarted: (): IAppThunkAction<KnownPaymentActions> => (dispatch, getState) => {
        const appState: IApplicationState = getState();
        const charge =
            appState.purchaseType.selectedPurchaseType === PurchaseTypes.GiftVoucher
                ? appState.giftVoucher.charge
                : appState.booking.charge;
        const isEmailSubscribed = appState.customer?.emailSubscribed ?? false;
        const isSmsSubscribed = appState.customer?.smsSubscribed ?? false;

        const phoneCountryCode = appState.customer?.countryCode;

        const isReservation = appState?.sites?.selectedSite?.reservationPortal ?? false;
        const isGiftVoucher = appState?.purchaseType?.selectedPurchaseType === PurchaseTypes.GiftVoucher;

        const country = appState?.sites?.selectedSite?.country ?? 'Unknown';
        const siteName = appState?.sites?.selectedSite?.name ?? 'Unknown';

        dispatch({
            type: PAYPAL_PAYMENT_REQUESTED,
        });

        if (charge) {
            BrazeEvents.PaymentInitiated(
                isEmailSubscribed,
                isSmsSubscribed,
                phoneCountryCode,
                isReservation,
                isGiftVoucher,
                charge,
                country,
                siteName
            );
        }
    },
    paypalPaymentCancelled: () => ({ type: PAYPAL_PAYMENT_CANCELLED } as IPaypalPaymentCancelledAction),
    paypalPaymentError: () => ({ type: PAYPAL_PAYMENT_FAILED } as IPaypalPaymentFailedAction),
    paypalPaymentSuccess: () => ({ type: PAYPAL_PAYMENT_SUCCESS } as IPaypalPaymentSuccessAction),

    checkoutPaymentStarted: (): IAppThunkAction<KnownPaymentActions> => (dispatch, getState) => {
        const appState: IApplicationState = getState();
        const charge =
            appState.purchaseType.selectedPurchaseType === PurchaseTypes.GiftVoucher
                ? appState.giftVoucher.charge
                : appState.booking.charge;
        const isEmailSubscribed = appState.customer?.emailSubscribed ?? false;
        const isSmsSubscribed = appState.customer?.smsSubscribed ?? false;

        const phoneCountryCode = appState.customer?.countryCode;

        const isReservation = appState?.sites?.selectedSite?.reservationPortal ?? false;
        const isGiftVoucher = appState?.purchaseType?.selectedPurchaseType === PurchaseTypes.GiftVoucher;

        const country = appState?.sites?.selectedSite?.country ?? 'Unknown';
        const siteName = appState?.sites?.selectedSite?.name ?? 'Unknown';

        dispatch({ type: CHECKOUT_PAYMENT_REQUESTED });

        if (charge) {
            BrazeEvents.PaymentInitiated(
                isEmailSubscribed,
                isSmsSubscribed,
                phoneCountryCode,
                isReservation,
                isGiftVoucher,
                charge,
                country,
                siteName
            );
        }
    },
    checkoutPaymentSuccess: () => ({ type: CHECKOUT_PAYMENT_SUCCESS } as ICheckoutPaymentSuccessAction),
    checkoutPaymentFailed: () => ({ type: CHECKOUT_PAYMENT_FAILED } as ICheckoutPaymentFailedAction),
    // Custom gateway: the user is about to be redirected to the provider. Flag loading and
    // fire the same payment-initiated analytics as the other gateways before navigating away.
    customPaymentStarted: (): IAppThunkAction<KnownPaymentActions> => (dispatch, getState) => {
        const appState: IApplicationState = getState();

        const isGiftVoucher = appState?.purchaseType?.selectedPurchaseType === PurchaseTypes.GiftVoucher ||
            appState?.purchaseType?.selectedPurchaseType === PurchaseTypes.GlobalGiftVoucher;
        const isReservation = appState?.sites?.selectedSite?.reservationPortal ?? false;

        const charge = isGiftVoucher ? appState.giftVoucher.charge : appState.booking.charge;
        const isEmailSubscribed = appState.customer?.emailSubscribed ?? false;
        const isSmsSubscribed = appState.customer?.smsSubscribed ?? false;

        const phoneCountryCode = appState.customer?.countryCode;

        const country = appState?.sites?.selectedSite?.country ?? 'Unknown';
        const siteName = appState?.sites?.selectedSite?.name ?? 'Unknown';

        dispatch({ type: CUSTOM_PAYMENT_REQUESTED });

        if (charge) {
            BrazeEvents.PaymentInitiated(
                isEmailSubscribed,
                isSmsSubscribed,
                phoneCountryCode,
                isReservation,
                isGiftVoucher,
                charge,
                country,
                siteName
            );
        }
    },

    backProcessingAdded: () => ({ type: BACK_PROCESSING_ADDED } as IBackProcessingAddedAction),

    validPaymentDetails: (gateway: string) =>
        ({
            type: VALID_PAYMENT_DETAILS,
            googleTagManagerEvent: GoogleTagManagerEvents.ValidPaymentDetails(gateway),
        } as IAnalyticsAction),
    viewedTermsOfService: () =>
        ({
            type: VIEWED_TERMS_OF_SERVICE,
            googleTagManagerEvent: GoogleTagManagerEvents.ClickedReadTOS(),
        } as IAnalyticsAction),
    viewedPrivacyPolicy: () =>
        ({
            type: VIEWED_PRIVACY_POLICY,
            googleTagManagerEvent: GoogleTagManagerEvents.ClickedReadPrivacyPolicy(),
        } as IAnalyticsAction),
    closedSurvey: () =>
        ({
            type: CLOSED_SURVEY,
            googleTagManagerEvent: GoogleTagManagerEvents.ClosedSurvey(),
        } as IAnalyticsAction),

    updateClientSecret: (clientSecret: string) =>
        ({
            type: UPDATE_CLIENT_SECRET,
            clientSecret: clientSecret,
        } as IUpdateClientSecretAction),

    clearClientSecret: () => ({ type: CLEAR_CLIENT_SECRET } as IClearClientSecretAction),
};

const undefinedState: IPaymentState = {
    stripe: {
        apiKey: undefined,
    },
    paypal: {
        apiKey: undefined,
    },
    checkout: {
        apiKey: undefined,
    },
    custom: undefined,
    loading: false,
    gatewaysLoading: false,
    backProcessing: false,
    clientSecret: undefined,
};

export const reducer: Reducer<IPaymentState> = (
    state: IPaymentState | undefined,
    incomingAction: Action
): IPaymentState => {
    const action: KnownPaymentActions = incomingAction as KnownPaymentActions;
    if (state === undefined) {
        console.log('Returning undefined payment state');
        return undefinedState;
    }

    switch (action.type) {
        case SITE_SELECTED: {
            return undefinedState;
        }
        case UPDATE_GIFT_VOUCHER_AMOUNT_SUCCESS:
        case CLEAR_GIFT_VOUCHER:
        case CANCEL_GIFT_VOUCHER_SUCCESS:
        case CANCEL_GIFT_VOUCHER_COMPLETE:
        case EXPIRE_BOOKING_SUCCESS:
        case EXPIRE_BOOKING_COMPLETE:
        case CLEAR_BOOKING:
        case DISCOUNT_CODE_REMOVED:
        case SET_SELECTED_PLAYERS:
        case INVOICE_CODE_SUCCESS:
        case INVOICE_CODE_REMOVED:
        case DISCOUNT_CODE_APPLIED:
        case UPDATE_BOOKING_REQUESTED:
        case UPDATE_PAYMENT_OPTION_REQUESTED:
        case UPDATE_ADDONS_COMPLETE: {
            return {
                ...state,
                clientSecret: undefined,
            };
        }
        case GET_PAYMENT_GATEWAYS_REQUESTED:
            return {
                ...state,
                gatewaysLoading: true,
            };
        case GET_PAYMENT_GATEWAYS_FAILED:
            return {
                ...state,
                gatewaysLoading: false,
            };
        case GET_PAYMENT_GATEWAYS_SUCCESS: {
            const paypalDetails =
                action.data.find((d) => d.gateway.toLowerCase() === PaymentGateway.PayPal.toLowerCase()) || undefined;
            const checkoutDetails =
                action.data.find((d) => d.gateway.toLowerCase() === PaymentGateway.Checkout.toLowerCase()) || undefined;
            const stripeDetails =
                action.data.find((d) => d.gateway.toLowerCase() === PaymentGateway.Stripe.toLowerCase()) || undefined;
            const customDetails =
                action.data.find((d) => d.gateway.toLowerCase() === PaymentGateway.Custom.toLowerCase()) || undefined;

            return {
                ...state,
                paypal: {
                    apiKey: paypalDetails?.apiKey ?? undefined,
                },
                checkout: {
                    apiKey: checkoutDetails?.apiKey ?? undefined,
                },
                stripe: {
                    apiKey: stripeDetails?.apiKey ?? undefined,
                    paymentMethodType: stripeDetails?.paymentMethodType ?? undefined,
                },
                custom: customDetails
                    ? {
                          gatewayName: customDetails.gatewayName ?? undefined,
                          imageUrl: customDetails.imageUrl ?? undefined,
                          paymentUrl: customDetails.paymentUrl ?? undefined,
                          paymentMethodType: customDetails.paymentMethodType ?? undefined,
                      }
                    : undefined,
                loading: false,
                gatewaysLoading: false,
            };
        }
        case STRIPE_PAYMENT_REQUESTED:
        case PAYPAL_PAYMENT_REQUESTED:
        case CHECKOUT_PAYMENT_REQUESTED:
        case CUSTOM_PAYMENT_REQUESTED: {
            return {
                ...state,
                loading: true,
            };
        }

        case STRIPE_PAYMENT_FAILED:
        case PAYPAL_PAYMENT_FAILED:
        case PAYPAL_PAYMENT_CANCELLED:
            return {
                ...state,
                loading: false,
            };

        case STRIPE_PAYMENT_COMPLETED:
        case PAYPAL_PAYMENT_COMPLETED:
        case CHECKOUT_PAYMENT_COMPLETED:
            return {
                ...state,
                loading: false,
            };
        case BACK_PROCESSING_ADDED:
            return {
                ...state,
                backProcessing: true,
            };

        case UPDATE_CLIENT_SECRET:
            return {
                ...state,
                clientSecret: action.clientSecret,
                backProcessing: true,
            };

        case CLEAR_CLIENT_SECRET:
            // prevent un-nessicary changes from triggering refreshed elsewhere in the app
            if (state.clientSecret === undefined) {
                return state;
            }

            return {
                ...state, 
                clientSecret: undefined
            };

        default:
            return state;
    }
};
